import axios from 'axios';
import { CacheKey, Environment, OperatingSystem } from '@signage-player/data-store';
import { CacheStoreTag } from '@signage-player/cache';
/**
 * Following properties and methods are currently not used and they are subject of removal in future.
 * Properties: app.adapter, app.defaultScreenColor
 */
export class ConfigService {
    constructor(env, version, appIdentifier = '@signage-player/electron', appName = 'electron') {
        this.appIdentifier = appIdentifier;
        this.appName = appName;
        this.dynamicConfig = {};
        this.cacheManager = null;
        this.publicIpRetryActive = false;
        this.publicIpRetryAttempts = 0;
        // Load environment variables (these should be available in main process)
        this.startTime = new Date();
        this.authUrl = env.VITE_AUTH_SERVER_URL;
        this.environment = env.VITE_APP_ENVIROMENT || Environment.Development;
        this.version = version;
        // Grouped initialization
        this.logging = {
            levels: this.parseArrayEnvVar(env.VITE_LOG_LEVELS),
            adapters: this.parseArrayEnvVar(env.VITE_LOGGER_ADAPTERS),
            logRequestContext: this.parseBooleanEnvVar(env.VITE_LOG_REQUEST_CONTEXT, true),
            logResponseContext: this.parseBooleanEnvVar(env.VITE_LOG_RESPONSE_CONTEXT, true),
            sentry: {
                dsn: env.VITE_SENTRY_DSN || undefined,
                minidumpDsn: env.VITE_SENTRY_MINIDUMP_DSN || undefined,
            },
            posthog: {
                apiKey: env.VITE_POSTHOG_API_KEY || undefined,
                host: env.VITE_POSTHOG_HOST || undefined,
            },
        };
        this.app = {
            adapter: env.VITE_APP_ADAPTER,
            soundEnabled: this.parseBooleanEnvVar(env.VITE_APP_SOUND_ENABLED),
            fadeDuration: this.parseNumberEnvVar(env.VITE_APP_FADE_DURATION, 1500),
            defaultScreenColor: env.VITE_DEFAULT_SCREEN_COLOR,
            isWayland: this.parseBooleanEnvVar(env.VITE_IS_WAYLAND),
            waylandResolution: this.parseResolutionEnvVar(env.VITE_WAYLAND_RESOLUTION),
            windowingBackend: this.parseWindowingBackendEnvVar(env.VITE_WINDOWING_BACKEND),
        };
        this.storage = {
            diskRoot: env.VITE_DISK_ROOT,
            limit: this.parseNumberEnvVar(env.VITE_STORAGE_LIMIT, 10240) * 1024 * 1024,
        };
        this.maintenance = {
            anydeskInstallPath: env.VITE_ANYDESK_INSTALL_PATH,
            registerWireguard: this.parseBooleanEnvVar(env.VITE_REGISTER_WIREGUARD, true),
        };
        this.emergencyEl = {
            debug: this.parseBooleanEnvVar(env.VITE_EMERGENCY_EL_DEBUG, false),
        };
        this.screens = {
            idleWakeupSeconds: this.parseOptionalNumberEnvVar(env.VITE_SCREEN_IDLE_WAKEUP_SECONDS),
            powerDebounceSeconds: this.parseNumberEnvVar(env.VITE_SCREEN_POWER_DEBOUNCE_SECONDS, 60),
        };
        // Initialize system information directly (no IPC needed)
        this.system = this.detectOperatingSystem();
        this.localIp = this.getLocalIpSync();
        this.publicIp = this.getPublicIp();
        this.publicIp.then(ip => {
            if (ip === 'OFFLINE') {
                this.startPublicIpRetryLoop();
            }
        });
    }
    startPublicIpRetryLoop() {
        if (this.publicIpRetryActive)
            return;
        this.publicIpRetryActive = true;
        const tick = async () => {
            const newIp = await this.getPublicIp();
            if (newIp !== 'OFFLINE') {
                this.publicIp = Promise.resolve(newIp);
                this.publicIpRetryActive = false;
                return;
            }
            // 30s for the first 10 minutes, then 5-minute cadence forever.
            this.publicIpRetryAttempts++;
            const delay = this.publicIpRetryAttempts < 20 ? 30000 : 5 * 60000;
            setTimeout(tick, delay);
        };
        setTimeout(tick, 10000);
    }
    setCacheManager(cacheManager) {
        this.cacheManager = cacheManager;
    }
    /**
     * Get local IP address synchronously
     */
    getLocalIpSync() {
        return '127.0.0.1'; // base config can not access network interfaces
    }
    /**
     * Detect operating system directly
     */
    detectOperatingSystem() {
        const platform = process.platform;
        if (platform === 'win32') {
            return OperatingSystem.Windows;
        }
        else {
            return OperatingSystem.Linux;
        }
    }
    /**
     * Get public IP address. Tries primary (ipify) then a secondary (icanhazip)
     */
    async getPublicIp() {
        const isIpv4 = (s) => typeof s === 'string' && /^\d{1,3}(\.\d{1,3}){3}$/.test(s);
        try {
            const response = await axios.get('https://api.ipify.org?format=json', { timeout: 10000 });
            const ip = response?.data?.ip;
            if (isIpv4(ip))
                return ip;
        }
        catch {
            // fall through to secondary
        }
        try {
            const response = await axios.get('https://ipv4.icanhazip.com/', { timeout: 10000, responseType: 'text' });
            const ip = typeof response?.data === 'string' ? response.data.trim() : '';
            if (isIpv4(ip))
                return ip;
        }
        catch {
            // both providers failed
        }
        return 'OFFLINE';
    }
    // Convenience methods
    isWindows() {
        return this.system === OperatingSystem.Windows;
    }
    isLinux() {
        return this.system === OperatingSystem.Linux;
    }
    isDevelopment() {
        return this.environment === Environment.Development;
    }
    isProduction() {
        return this.environment === Environment.Production;
    }
    async loadDynamicConfig(axios, authInstance) {
        try {
            const dynamicConfig = (await axios.client.get('api/v1/devices/' + authInstance.hash + '/dynamic-config')).data;
            this.dynamicConfig = {
                reverb: dynamicConfig.reverb_config
                    ? {
                        channel: dynamicConfig.channel,
                        key: dynamicConfig.reverb_config.key,
                        host: dynamicConfig.reverb_config.host,
                        port: dynamicConfig.reverb_config.port,
                        scheme: dynamicConfig.reverb_config.scheme,
                        path: dynamicConfig.reverb_config.path ? dynamicConfig.reverb_config.path : undefined,
                    }
                    : null,
                websocketsEnabled: dynamicConfig.websockets_enabled || false,
                timeSyncAdapter: dynamicConfig.time_sync_adapter || null,
                overrides: dynamicConfig.overrides ? this.parseOverrides(dynamicConfig.overrides) : undefined,
            };
            this.applyOverrides(this.dynamicConfig.overrides);
            this.cacheManager?.tag(CacheStoreTag.Persistent)?.set(CacheKey.DynamicConfig, this.dynamicConfig);
            return this.dynamicConfig;
        }
        catch (err) {
            const dynamicConfig = (await this.cacheManager?.tag(CacheStoreTag.Persistent)?.get(CacheKey.DynamicConfig));
            if (dynamicConfig) {
                this.dynamicConfig = dynamicConfig;
                this.applyOverrides(this.dynamicConfig.overrides);
                return this.dynamicConfig;
            }
            throw err;
        }
    }
    parseOverrides(raw) {
        const overrides = {};
        if (raw.logging) {
            overrides.logging = {};
            if (Array.isArray(raw.logging.levels)) {
                overrides.logging.levels = raw.logging.levels;
            }
            if (raw.logging.log_request_context !== undefined) {
                overrides.logging.logRequestContext = raw.logging.log_request_context;
            }
            if (raw.logging.log_response_context !== undefined) {
                overrides.logging.logResponseContext = raw.logging.log_response_context;
            }
        }
        if (raw.storage) {
            overrides.storage = {};
            if (raw.storage.limit !== undefined) {
                overrides.storage.limit = raw.storage.limit;
            }
        }
        if (raw.app) {
            overrides.app = {};
            if (raw.app.sound_enabled !== undefined) {
                overrides.app.soundEnabled = raw.app.sound_enabled;
            }
            if (raw.app.fade_duration !== undefined) {
                overrides.app.fadeDuration = raw.app.fade_duration;
            }
            if (raw.app.wayland_resolution !== undefined) {
                overrides.app.waylandResolution = raw.app.wayland_resolution ? this.parseResolutionEnvVar(raw.app.wayland_resolution) : null;
            }
            if (raw.app.windowing_backend !== undefined) {
                // Validate the enum here rather than assigning raw: the field is typed
                // 'x11'|'wayland'|'auto' and its only consumer (persistBackendPreference) rejects junk,
                // but an unvalidated assignment lets a bad server value masquerade as a valid one for any
                // future consumer. Invalid values are ignored (baked/marker value stands), not coerced.
                if (raw.app.windowing_backend === 'x11' || raw.app.windowing_backend === 'wayland' || raw.app.windowing_backend === 'auto') {
                    overrides.app.windowingBackend = raw.app.windowing_backend;
                }
            }
        }
        if (raw.emergency_el) {
            overrides.emergencyEl = {};
            if (raw.emergency_el.debug !== undefined) {
                overrides.emergencyEl.debug = raw.emergency_el.debug;
            }
        }
        return Object.keys(overrides).length > 0 ? overrides : undefined;
    }
    applyOverrides(overrides) {
        if (!overrides)
            return;
        if (overrides.logging) {
            if (overrides.logging.levels) {
                // Replace the whole array so the server can both narrow (drop levels)
                // and widen (add levels). Positional overwrite silently dropped
                // entries beyond the override length, breaking remote level changes.
                // Keep the same array reference so any consumer holding a reference
                // sees the new levels.
                this.logging.levels.length = 0;
                this.logging.levels.push(...overrides.logging.levels);
            }
            if (overrides.logging.logRequestContext !== undefined) {
                this.logging.logRequestContext = overrides.logging.logRequestContext;
            }
            if (overrides.logging.logResponseContext !== undefined) {
                this.logging.logResponseContext = overrides.logging.logResponseContext;
            }
        }
        if (overrides.storage?.limit !== undefined) {
            this.storage.limit = overrides.storage.limit * 1024 * 1024;
        }
        if (overrides.app?.soundEnabled !== undefined) {
            this.app.soundEnabled = overrides.app.soundEnabled;
        }
        if (overrides.app?.fadeDuration !== undefined) {
            this.app.fadeDuration = overrides.app.fadeDuration;
        }
        if (overrides.app?.waylandResolution !== undefined) {
            this.app.waylandResolution = overrides.app.waylandResolution;
        }
        if (overrides.app?.windowingBackend !== undefined) {
            this.app.windowingBackend = overrides.app.windowingBackend;
        }
        if (overrides.emergencyEl?.debug !== undefined) {
            this.emergencyEl.debug = overrides.emergencyEl.debug;
        }
    }
    parseArrayEnvVar(value) {
        if (!value)
            return [];
        return value
            .split(',')
            .map(v => v.trim())
            .filter(v => v.length > 0);
    }
    parseNumberEnvVar(value, defaultValue) {
        if (value === undefined)
            return defaultValue;
        const parsed = parseInt(String(value), 10);
        return isNaN(parsed) ? defaultValue : parsed;
    }
    /**
     * Parse an optional numeric env var. Returns null when the variable is unset,
     * empty, "null", or parses to 0 / NaN — i.e. the feature the caller gates on
     * this value is considered disabled.
     */
    parseOptionalNumberEnvVar(value) {
        if (value === undefined)
            return null;
        const trimmed = String(value).trim();
        if (trimmed === '' || trimmed.toLowerCase() === 'null')
            return null;
        const parsed = parseInt(trimmed, 10);
        if (isNaN(parsed) || parsed <= 0)
            return null;
        return parsed;
    }
    parseBooleanEnvVar(value, defaultValue = false) {
        if (value === undefined)
            return defaultValue;
        return String(value).toLowerCase() === 'true';
    }
    parseResolutionEnvVar(value) {
        if (!value || !value.trim())
            return null;
        const parts = value.trim().split('x');
        if (parts.length !== 2)
            return null;
        const width = parseInt(parts[0], 10);
        const height = parseInt(parts[1], 10);
        if (isNaN(width) || isNaN(height) || width <= 0 || height <= 0)
            return null;
        return { width, height };
    }
    parseWindowingBackendEnvVar(value) {
        return value === 'x11' || value === 'wayland' || value === 'auto' ? value : 'x11';
    }
}
export default ConfigService;
//# sourceMappingURL=ConfigService.js.map